import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, AlertCircle, Megaphone, Tag } from 'lucide-react';
import {
  useGetCategoryBySlugQuery,
  useGetCategoryBreadcrumbQuery,
  useGetAdsQuery,
} from '@nimbazaar/store';
import { LucideIcon } from '../components/LucideIcon';
import { AdCard, apiAdToCardProps } from '../components/AdCard';
import { Pagination } from '../components/Pagination';

function LoadingSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="h-4 bg-gray-200 rounded w-48 mb-6" />
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gray-200 rounded-xl" />
            <div className="space-y-2">
              <div className="h-6 bg-gray-200 rounded w-48" />
              <div className="h-4 bg-gray-200 rounded w-64" />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="h-44 bg-gray-200" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-5 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get('page') || '1', 10);

  const {
    data: catData,
    isLoading: isCatLoading,
    isError: isCatError,
    refetch: refetchCat,
  } = useGetCategoryBySlugQuery(slug || '', { skip: !slug });

  const category = catData?.data;

  const {
    data: breadcrumbData,
  } = useGetCategoryBreadcrumbQuery(category?.id || '', {
    skip: !category?.id,
  });

  const breadcrumbs = breadcrumbData?.data?.breadcrumbs ?? [];

  const { data: adsData, isLoading: isAdsLoading } = useGetAdsQuery(
    { categoryId: category?.id, page, limit: 20 },
    { skip: !category?.id }
  );

  const ads = adsData?.data ?? [];
  const meta = adsData?.meta;

  if (isCatLoading) {
    return <LoadingSkeleton />;
  }

  if (isCatError || !category) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Category Not Found</h2>
          <p className="text-gray-600 mb-6">
            The category you're looking for doesn't exist or has been removed.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link
              to="/"
              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
            >
              Go to Homepage
            </Link>
            <button
              onClick={refetchCat}
              className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-100 font-medium rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6 flex-wrap">
          <Link to="/" className="text-gray-500 hover:text-primary-500 transition-colors">
            Home
          </Link>
          {breadcrumbs.map((crumb) => (
            <span key={crumb.id} className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-gray-400" />
              {crumb.id === category.id ? (
                <span className="text-gray-900 font-medium">{crumb.name}</span>
              ) : (
                <Link
                  to={`/category/${crumb.slug}`}
                  className="text-gray-500 hover:text-primary-500 transition-colors"
                >
                  {crumb.name}
                </Link>
              )}
            </span>
          ))}
          {/* If no breadcrumbs loaded yet, show current category name */}
          {breadcrumbs.length === 0 && (
            <>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-900 font-medium">{category.name}</span>
            </>
          )}
        </nav>

        {/* Category Header */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 bg-primary-50 rounded-xl flex items-center justify-center">
              <LucideIcon
                name={category.icon || ''}
                className="w-5 h-5 sm:w-7 sm:h-7 text-primary-500"
                fallback={<Tag className="w-5 h-5 sm:w-7 sm:h-7 text-primary-500" />}
              />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{category.name}</h1>
              {category.description && (
                <p className="text-sm text-gray-500 mt-1">{category.description}</p>
              )}
              {category.adCount != null && (
                <p className="text-xs text-gray-400 mt-1">
                  {category.adCount} {category.adCount === 1 ? 'ad' : 'ads'}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Subcategory Chips */}
        {category.children && category.children.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {category.children.map((sub) => (
              <Link
                key={sub.id}
                to={`/category/${sub.slug}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-primary-300 hover:text-primary-600 hover:bg-primary-50 transition-colors"
              >
                <LucideIcon
                  name={sub.icon || ''}
                  className="w-3.5 h-3.5"
                  fallback={<Tag className="w-3.5 h-3.5" />}
                />
                {sub.name}
                {sub.adCount != null && (
                  <span className="text-xs text-gray-400">({sub.adCount})</span>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Ads Grid */}
        {isAdsLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse"
              >
                <div className="h-44 bg-gray-200" />
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4" />
                  <div className="h-5 bg-gray-200 rounded w-1/2" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No ads in this category</h2>
            <p className="text-gray-500">Be the first to post a wanted ad here!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
              {ads.map((ad) => {
                const props = apiAdToCardProps(ad);
                return <AdCard key={ad.id} {...props} fullWidth />;
              })}
            </div>

            {meta && (
              <div className="mt-8">
                <Pagination
                  page={meta.page}
                  totalPages={meta.totalPages}
                  hasNextPage={meta.hasNextPage}
                  hasPrevPage={meta.hasPrevPage}
                  onPageChange={(p) => {
                    const next = new URLSearchParams(searchParams);
                    if (p > 1) {
                      next.set('page', String(p));
                    } else {
                      next.delete('page');
                    }
                    setSearchParams(next);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
